import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  Button,
  Spin,
  Result,
  Typography,
  message,
  Divider,
} from 'antd';
import {
  HeartFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import PatientTokenService from '../../services/PatientTokenService';
import {
  PageWrapper,
  BrandHeader,
  FormCard,
  SectionTitle,
} from './styled';

const { Title, Text } = Typography;

const PatientComplete = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | valid | expired | completed | error | success
  const [patientName, setPatientName] = useState('');
  const [sections, setSections] = useState([]);
  const [fields, setFields] = useState([]);
  const [initialValues, setInitialValues] = useState({});

  const [form] = Form.useForm();

  // Validate token and load form config
  const initialize = useCallback(async () => {
    try {
      setLoading(true);
      const validateResult = await PatientTokenService.validateToken(token);
      if (!validateResult) {
        setStatus('error');
        return;
      }

      setPatientName(`${validateResult.patient?.firstName || ''} ${validateResult.patient?.lastName || ''}`);
      setSections(validateResult.sections || []);
      setFields(validateResult.fields || []);

      // Load current data
      try {
        const formData = await PatientTokenService.getFormData(token);
        if (formData) {
          setInitialValues(formData);
          form.setFieldsValue(formData);
        }
      } catch {
        // No existing data — that's fine
      }

      setStatus('valid');
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('expired') || err?.status === 410) {
        setStatus('expired');
      } else if (msg.includes('completed')) {
        setStatus('completed');
      } else {
        setStatus('error');
      }
    } finally {
      setLoading(false);
    }
  }, [token, form]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Submit
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await PatientTokenService.submitForm(token, values);
      setStatus('success');
      message.success('Your information has been saved!');
    } catch (err) {
      message.error(err?.message || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render a field based on its type
  const renderField = (field) => {
    const { name, label, type, required, options = [] } = field;

    const rules = required ? [{ required: true, message: `${label} is required` }] : [];

    switch (type) {
      case 'text':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Input placeholder={label} size="large" />
          </Form.Item>
        );

      case 'email':
        return (
          <Form.Item
            key={name}
            name={name}
            label={label}
            rules={[...rules, { type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder={label} size="large" type="email" />
          </Form.Item>
        );

      case 'phone':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Input placeholder="(555) 555-5555" size="large" type="tel" />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Input placeholder={label} size="large" type="number" />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Input.TextArea placeholder={label} rows={3} size="large" />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <DatePicker
              size="large"
              style={{ width: '100%' }}
              format="MM/DD/YYYY"
              placeholder="Select date"
            />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Select
              size="large"
              placeholder={`Select ${label.toLowerCase()}`}
              options={options.map(o => ({ label: o.label, value: o.value }))}
            />
          </Form.Item>
        );

      case 'radio':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Radio.Group>
              {options.map(o => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item key={name} name={name} label={label} rules={rules} valuePropName="value">
            <Checkbox.Group options={options.map(o => ({ label: o.label, value: o.value }))} />
          </Form.Item>
        );

      default:
        return (
          <Form.Item key={name} name={name} label={label} rules={rules}>
            <Input placeholder={label} size="large" />
          </Form.Item>
        );
    }
  };

  // Group fields by section
  const fieldsBySection = {};
  for (const field of fields) {
    if (!fieldsBySection[field.section]) {
      fieldsBySection[field.section] = [];
    }
    fieldsBySection[field.section].push(field);
  }

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  // ── RENDER ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageWrapper>
        <BrandHeader>
          <HeartFilled style={{ fontSize: 40, color: '#e74c3c' }} />
          <Title level={3} style={{ margin: '8px 0 0', color: '#333' }}>
            Black Heart Association
          </Title>
        </BrandHeader>
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>Loading your form...</Text>
        </div>
      </PageWrapper>
    );
  }

  if (status === 'expired') {
    return (
      <PageWrapper>
        <BrandHeader>
          <HeartFilled style={{ fontSize: 40, color: '#e74c3c' }} />
        </BrandHeader>
        <Result
          icon={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
          title="Link Expired"
          subTitle="This link has expired. Please contact the Black Heart Association for a new one."
        />
      </PageWrapper>
    );
  }

  if (status === 'completed' || status === 'success') {
    return (
      <PageWrapper>
        <BrandHeader>
          <HeartFilled style={{ fontSize: 40, color: '#e74c3c' }} />
        </BrandHeader>
        <Result
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="Thank You!"
          subTitle="Your information has been saved successfully. You will receive an email with your results."
        />
      </PageWrapper>
    );
  }

  if (status === 'error') {
    return (
      <PageWrapper>
        <BrandHeader>
          <HeartFilled style={{ fontSize: 40, color: '#e74c3c' }} />
        </BrandHeader>
        <Result
          icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
          title="Invalid Link"
          subTitle="This link is not valid. Please check the URL or contact the Black Heart Association."
        />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BrandHeader>
        <HeartFilled style={{ fontSize: 40, color: '#e74c3c' }} />
        <Title level={3} style={{ margin: '8px 0 0', color: '#333' }}>
          Black Heart Association
        </Title>
        <Text type="secondary">
          Complete Your Health Information
        </Text>
      </BrandHeader>

      <FormCard>
        {patientName && (
          <Text style={{ display: 'block', marginBottom: 16, fontSize: 16 }}>
            Welcome back, <strong>{patientName.trim()}</strong>! Please complete the remaining fields below.
          </Text>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={initialValues}
          size="large"
        >
          {sortedSections.map((section, idx) => {
            const sectionFields = (fieldsBySection[section.key] || [])
              .sort((a, b) => a.order - b.order);

            if (sectionFields.length === 0) return null;

            return (
              <div key={section.key}>
                {idx > 0 && <Divider />}
                <SectionTitle>{section.label}</SectionTitle>
                {sectionFields.map(renderField)}
              </div>
            );
          })}

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              size="large"
              style={{
                height: 48,
                fontSize: 16,
                borderRadius: 8,
                background: '#e74c3c',
                borderColor: '#e74c3c',
              }}
            >
              {submitting ? 'Saving...' : 'Submit'}
            </Button>
          </Form.Item>
        </Form>
      </FormCard>
    </PageWrapper>
  );
};

export default PatientComplete;
